"use client";

import {
  calculateCumulativeDistanceMeters,
  formatElapsedToHundredths,
} from "@hisani/stopwatch";
import { Button } from "@hisani/ui";
import {
  ChevronDown,
  Clipboard,
  History,
  MessageSquareText,
  Share2,
  Trash2,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";
import type {
  CompletedStopwatchSession,
  PersistedTimerSnapshot,
} from "./stopwatch-storage";
import {
  calculateSpeed,
  coveredDistanceMeters,
  formatAverageSpeed,
  formatSplitDistance,
} from "./stopwatch-workspace-model";

interface StopwatchHistoryProps {
  readonly sessions: readonly CompletedStopwatchSession[];
  readonly locale: string;
  readonly isLoading: boolean;
  readonly hasMore: boolean;
  readonly notice: string | null;
  readonly onCopy: (session: CompletedStopwatchSession) => void;
  readonly onShare: (session: CompletedStopwatchSession) => void;
  readonly onDelete: (session: CompletedStopwatchSession) => void;
  readonly onEditComment: (
    session: CompletedStopwatchSession,
    timer: PersistedTimerSnapshot,
    timerIndex: number,
  ) => void;
  readonly onLoadMore: () => void;
}

export function StopwatchHistory({
  sessions,
  locale,
  isLoading,
  hasMore,
  notice,
  onCopy,
  onShare,
  onDelete,
  onEditComment,
  onLoadMore,
}: StopwatchHistoryProps) {
  const t = useTranslations("Stopwatch");
  const [expanded, setExpanded] = useState(false);
  const historyContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    const historyContent = historyContentRef.current;
    const scrollTarget =
      historyContent?.querySelector("article") ?? historyContent;
    scrollTarget?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
      inline: "nearest",
    });
  }, [expanded]);

  return (
    <section aria-labelledby="stopwatch-history-heading">
      <h2 className="sticky bottom-0 z-[var(--z-index-sticky)] border-t border-border-default bg-canvas text-heading-medium md:static md:z-auto">
        <button
          type="button"
          className="flex min-h-11 w-full items-center justify-between gap-3 rounded-md bg-transparent text-start text-primary hover:bg-transparent active:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          aria-expanded={expanded}
          aria-controls="stopwatch-history-items"
          onClick={() => setExpanded((current) => !current)}
        >
          <span id="stopwatch-history-heading">{t("historyTitle")}</span>
          <ChevronDown
            className={`size-5 transition-transform duration-[var(--motion-duration-fast)] motion-reduce:transition-none ${
              expanded ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </button>
      </h2>

      <div id="stopwatch-history-items" hidden={!expanded} className="pb-4">
        <p
          className={
            notice === null
              ? "sr-only"
              : "mt-2 min-h-5 text-sm font-medium text-secondary"
          }
          role="status"
          aria-live="polite"
        >
          {notice}
        </p>

        <div ref={historyContentRef} className="mt-2">
          {isLoading && sessions.length === 0 ? (
            <p className="rounded-lg border border-border-subtle bg-subtle px-3 py-4 text-center text-sm text-secondary">
              {t("loadingHistory")}
            </p>
          ) : sessions.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-default bg-surface px-4 py-5 text-center">
              <History
                className="mx-auto size-5 text-brand-ink"
                aria-hidden="true"
              />
              <p className="mt-2 text-sm font-bold text-primary">
                {t("historyEmptyTitle")}
              </p>
              <p className="mx-auto mt-1 max-w-lg text-sm leading-5 text-secondary">
                {t("historyEmptyDescription")}
              </p>
            </div>
          ) : (
            <div className="grid gap-2">
              {sessions.map((session) => (
                <HistoryCard
                  key={session.id}
                  session={session}
                  locale={locale}
                  onCopy={onCopy}
                  onShare={onShare}
                  onDelete={onDelete}
                  onEditComment={onEditComment}
                />
              ))}
            </div>
          )}
        </div>

        {hasMore ? (
          <div className="mt-3 flex justify-center">
            <Button
              variant="secondary"
              onClick={onLoadMore}
              disabled={isLoading}
            >
              {isLoading ? t("loadingHistory") : t("loadMoreHistory")}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function HistoryCard({
  session,
  locale,
  onCopy,
  onShare,
  onDelete,
  onEditComment,
}: {
  readonly session: CompletedStopwatchSession;
  readonly locale: string;
  readonly onCopy: (session: CompletedStopwatchSession) => void;
  readonly onShare: (session: CompletedStopwatchSession) => void;
  readonly onDelete: (session: CompletedStopwatchSession) => void;
  readonly onEditComment: (
    session: CompletedStopwatchSession,
    timer: PersistedTimerSnapshot,
    timerIndex: number,
  ) => void;
}) {
  const t = useTranslations("Stopwatch");
  const completedDate = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(session.completedAtEpochMilliseconds);

  return (
    <article className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-bold text-primary">
            {completedDate}
          </h3>
          <p className="text-xs text-secondary">
            {t("historyRunCount", { count: session.timers.length })}
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            variant="secondary"
            size="sm"
            className="min-w-11 px-2"
            onClick={() => onCopy(session)}
            aria-label={t("copyHistorySession", { date: completedDate })}
          >
            <Clipboard className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="min-w-11 px-2"
            onClick={() => onShare(session)}
            aria-label={t("shareHistorySession", { date: completedDate })}
          >
            <Share2 className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="min-w-11 px-2"
            onClick={() => onDelete(session)}
            aria-label={t("deleteHistorySession", { date: completedDate })}
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <details className="group border-t border-border-subtle">
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 py-2 text-sm font-bold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus">
          {t("viewSession")}
          <ChevronDown
            className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
        </summary>
        <div className="grid gap-2 border-t border-border-subtle bg-subtle p-3">
          {session.timers.map((timer, index) => (
            <HistoryRun
              key={timer.run.id}
              timer={timer}
              index={index}
              locale={locale}
              onEditComment={() => onEditComment(session, timer, index)}
            />
          ))}
        </div>
      </details>
    </article>
  );
}

function HistoryRun({
  timer,
  index,
  locale,
  onEditComment,
}: {
  readonly timer: PersistedTimerSnapshot;
  readonly index: number;
  readonly locale: string;
  readonly onEditComment: () => void;
}) {
  const t = useTranslations("Stopwatch");
  const label =
    timer.run.label?.trim() || t("timerDefault", { number: index + 1 });
  const distance = formatDistance(coveredDistanceMeters(timer), locale, t);
  const speed = calculateSpeed(timer, timer.run.elapsedMilliseconds);
  const splits = timer.run.sectionals.map((sectional, sectionalIndex) => ({
    sectional,
    cumulativeDistanceMeters: calculateCumulativeDistanceMeters(
      timer.run.sectionals.slice(0, sectionalIndex + 1),
    ),
  }));

  return (
    <section className="rounded-md border border-border-subtle bg-surface px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-bold text-primary">{label}</h4>
        <div className="flex items-center gap-1">
          <p className="font-mono text-xl font-bold text-primary tabular-nums">
            {formatElapsedToHundredths(timer.run.elapsedMilliseconds)}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="min-w-11 px-2"
            onClick={onEditComment}
            aria-label={t("editTimerComment", { label })}
          >
            <MessageSquareText className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary">
        <span>{t("historyDistance", { distance })}</span>
        <span>
          {t("historySpeed", {
            speed:
              speed === null
                ? t("notAvailable")
                : formatAverageSpeed(speed, locale, t),
          })}
        </span>
        <span>{t("splitCount", { count: timer.run.sectionals.length })}</span>
      </div>
      {timer.comment === null ? null : (
        <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-subtle px-3 py-2 text-sm leading-5 text-secondary">
          <span className="sr-only">{t("commentFieldLabel")}: </span>
          {timer.comment}
        </p>
      )}
      {timer.run.sectionals.length > 0 ? (
        <ol className="mt-2 grid gap-1 border-t border-border-subtle pt-2 text-xs">
          {splits.map(({ sectional, cumulativeDistanceMeters }) => (
            <li
              key={sectional.sequence}
              className="flex flex-wrap justify-between gap-2"
            >
              <span className="font-semibold text-primary">
                {t("splitNumberValue", { number: sectional.sequence })}
              </span>
              <span className="font-mono text-secondary tabular-nums">
                {t("historySplitTimes", {
                  cumulativeDistance: formatSplitDistance(
                    cumulativeDistanceMeters,
                    locale,
                    t,
                  ),
                  cumulativeTime: formatElapsedToHundredths(
                    sectional.cumulativeMilliseconds,
                  ),
                  sectionDistance: formatSplitDistance(
                    sectional.distanceMeters,
                    locale,
                    t,
                  ),
                  sectionalTime: formatElapsedToHundredths(
                    sectional.splitMilliseconds,
                  ),
                })}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

function formatDistance(
  distanceMeters: number,
  locale: string,
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
): string {
  const formattedValue = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  }).format(distanceMeters);
  if (Number.isInteger(distanceMeters / 200) && distanceMeters >= 200) {
    return t("distanceOption", {
      furlongs: distanceMeters / 200,
      meters: distanceMeters,
    });
  }
  return t("distanceMeters", { value: formattedValue });
}
